import dayjs from 'dayjs'
import { IcsEvent } from 'ts-ics'

import { useLocalization } from '@/shared/composables/localization'
import { getDayJSInTimezone } from '@/shared/composables/timezone'
import { BIRTH_DAY_DATE_TIME_FORMAT } from '@/shared/utils/constants'

const ICS_DAY_TO_LOCALE_KEY: Record<string, string> = {
  MO: 'mo',
  TU: 'tu',
  WE: 'we',
  TH: 'th',
  FR: 'fr',
  SA: 'sa',
  SU: 'su',
}

const ICS_DAY_ORDER: Record<string, number> = {
  MO: 1,
  TU: 2,
  WE: 3,
  TH: 4,
  FR: 5,
  SA: 6,
  SU: 7,
}

const formatEventDateTime = (event: IcsEvent, format: string): string => (
  getDayJSInTimezone(dayjs(event.start.date).unix()).format(format)
)

const formatTimeSuffix = (event: IcsEvent): string => {
  const time = formatEventDateTime(event, 'HH:mm')
  return `${useLocalization().t('calendar.in')} ${time}`
}

const formatMonthlyDays = (monthDays: number[], t: (key: string) => string, timeSuffix: string): string => {
  const sorted = [...monthDays].sort((a, b) => a - b)
  const [last, ...rest] = [...sorted].reverse()

  if (!rest.length) {
    return `${last} ${t('common.daysOfEachMonth')} ${timeSuffix}`
  }

  rest.reverse()
  return `${rest.join(', ')} ${t('common.and')} ${last} ${t('common.daysOfEachMonth')} ${timeSuffix}`
}

const sortWeekdays = (weekdays: string[]): string[] => {
  return [...weekdays].sort((a, b) => (ICS_DAY_ORDER[a] ?? 0) - (ICS_DAY_ORDER[b] ?? 0))
}

export const formatScheduleNotificationText = (event: IcsEvent): string => {
  const { t, tc } = useLocalization()
  const recurrenceRule = event.recurrenceRule

  if (!recurrenceRule) {
    return `${t('calendar.scheduleStartedAt')} ${formatEventDateTime(event, BIRTH_DAY_DATE_TIME_FORMAT)}`
  }

  const timeSuffix = formatTimeSuffix(event)

  if (recurrenceRule.frequency === 'DAILY') {
    const interval = recurrenceRule.interval ?? 1
    if (interval > 1) {
      return `${tc('common.every', interval)} ${interval} ${tc('common.days', interval)} ${timeSuffix}`
    }
    return `${t('calendar.everyDay')} ${timeSuffix}`
  }

  if (recurrenceRule.frequency === 'WEEKLY') {
    const weekdays = recurrenceRule.byDay?.flatMap(({ day }) => day ? [day] : []) ?? []
    if (!weekdays.length) {
      return ''
    }

    const isEveryWorkDay = weekdays.length === 5
      && !weekdays.includes('SU')
      && !weekdays.includes('SA')

    if (isEveryWorkDay) {
      return `${t('calendar.everyWorkDay')} ${timeSuffix}`
    }

    const weekdayLabels = sortWeekdays(weekdays)
      .map(day => t(`common.${ICS_DAY_TO_LOCALE_KEY[day] ?? day}`).toLowerCase())

    return `${t('common.by')} ${weekdayLabels.join(', ')} ${timeSuffix}`
  }

  if (recurrenceRule.frequency === 'MONTHLY') {
    const monthDays = recurrenceRule.byMonthday ?? []
    if (!monthDays.length) {
      return ''
    }

    return formatMonthlyDays(monthDays, t, timeSuffix)
  }

  return `${t('calendar.scheduleStartedAt')} ${formatEventDateTime(event, BIRTH_DAY_DATE_TIME_FORMAT)}`
}
